import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { PublicHeader, PublicFooter } from "./PublicLayout";
import { UserPlus, MailCheck, LayoutDashboard, ArrowRight, Handshake, Rocket, Users2,
  ShieldCheck, TrendingUp, HeartHandshake, Cpu } from "lucide-react";

const CAREER_PATHS = [
  {
    key: "buy_agency",
    title: "Buy an Existing Agency",
    body: "Acquire an established insurance business with an existing book of customers, infrastructure, and revenue history.",
    icon: Handshake,
    img: "/home/buy-agency.jpg",
    alt: "Agent and buyer shaking hands outside a Farmers Insurance agency after an agency purchase",
  },
  {
    key: "start_agency",
    title: "Start an Agency from Scratch",
    body: "Build an insurance business from the ground up — develop your own team, customer base, and long-term business value.",
    icon: Rocket,
    img: "/home/start-agency.jpg",
    alt: "New agent planning a business with a mentor at a Farmers Insurance office, business plan and marketing guides on the table",
  },
  {
    key: "work_in_agency",
    title: "Work in an Agency",
    body: "Explore sales, service, operations, producer, and support careers working within an insurance agency.",
    icon: Users2,
    img: "/home/work-in-agency.jpg",
    alt: "Two professionals working together at a desk inside a Farmers Insurance agency office",
  },
];

const STEPS = [
  { icon: UserPlus, title: "Create Your Account", body: "Tell us about yourself and the career path that interests you." },
  { icon: MailCheck, title: "Verify & Get Reviewed", body: "Confirm your email; our recruiting team reviews new accounts before access is granted." },
  { icon: LayoutDashboard, title: "Access Your Portal", body: "Once approved, access your recruiter, tasks, documents, appointments, and progress." },
];

const VALUE = [
  { icon: HeartHandshake, title: "Relationship-based advising", body: "People still turn to trusted advisors to protect what matters — a human strength technology complements rather than replaces." },
  { icon: ShieldCheck, title: "Helping manage risk", body: "Individuals and businesses will always need guidance navigating insurance and financial decisions." },
  { icon: TrendingUp, title: "Entrepreneurship & ownership", body: "Build local business value, recurring customer relationships, and a path to grow and lead a team." },
  { icon: Cpu, title: "Technology & AI as leverage", body: "Modern tools can boost productivity and customer service, letting professionals focus on advising and growth." },
];

export default function PublicHome() {
  const [info, setInfo] = useState(null);
  useEffect(() => { api.get("/public/site-info").then((r) => setInfo(r.data)).catch(() => {}); }, []);

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <PublicHeader />

      {/* Hero */}
      <section className="relative overflow-hidden text-white"
        style={{ background: "radial-gradient(1100px 520px at 18% -10%, #0090D6 0%, transparent 55%), linear-gradient(160deg,#013A5A 0%,#006090 60%,#00527D 100%)" }}>
        <div className="max-w-6xl mx-auto px-6 py-20 lg:py-28 relative z-10">
          <p className="text-brand-cyan font-semibold tracking-wide uppercase text-xs mb-4" data-testid="hero-eyebrow">
            Careers in Insurance & Financial Services
          </p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] max-w-3xl" data-testid="hero-title">
            Build a Career You Own
          </h1>
          <p className="mt-6 text-white/80 text-lg max-w-2xl leading-relaxed">
            Explore opportunities in insurance, financial services, and agency ownership — whether you want to
            buy an established agency, start one from scratch, or grow your career working within an agency.
          </p>
          <div className="mt-9 flex flex-wrap gap-3">
            <a href="#paths"><Button data-testid="hero-explore" className="bg-[#0D9488] hover:bg-[#0b7d72] text-white rounded-full px-7 h-12 text-base">
              Explore Opportunities <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button></a>
            <Link to="/register"><Button data-testid="hero-create-account" variant="outline" className="rounded-full px-7 h-12 text-base border-white/40 text-white bg-white/0 hover:bg-white/10">
              Create an Account
            </Button></Link>
          </div>
        </div>
        <div className="absolute -right-24 -bottom-24 w-[380px] h-[380px] rounded-full border border-white/10" />
        <div className="absolute right-40 top-16 w-32 h-32 rounded-full border border-white/10" />
      </section>

      <main className="max-w-6xl mx-auto px-6 w-full flex-1">
        {/* Career path cards */}
        <section id="paths" className="-mt-12 relative z-20 grid md:grid-cols-3 gap-5 scroll-mt-20">
          {CAREER_PATHS.map((p) => (
            <div key={p.key} data-testid={`career-card-${p.key}`}
              className="group flex flex-col rounded-2xl bg-white border border-slate-200 shadow-[0_10px_40px_-20px_rgba(0,96,144,0.35)] overflow-hidden hover:-translate-y-1 transition-transform">
              <div className="relative h-44 bg-slate-100 overflow-hidden">
                <img src={p.img} alt={p.alt} loading="lazy" decoding="async"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                <span className="absolute top-3 left-3 w-10 h-10 rounded-xl bg-white/90 text-[#006090] flex items-center justify-center shadow">
                  <p.icon className="w-5 h-5" />
                </span>
              </div>
              <div className="flex flex-col flex-1 p-6">
                <h3 className="font-display font-bold text-slate-900 text-lg">{p.title}</h3>
                <p className="mt-1.5 text-sm text-slate-600 leading-relaxed flex-1">{p.body}</p>
                <Link to={`/register?interest=${p.key}`} data-testid={`career-explore-${p.key}`}
                  className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-[#0D9488] group-hover:gap-2 transition-all">
                  Learn More <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          ))}
        </section>

        {/* Value proposition */}
        <section className="mt-20">
          <h2 className="font-display text-2xl font-bold text-slate-900">Why insurance & financial services?</h2>
          <p className="text-sm text-slate-500 mt-1 max-w-2xl">A durable, people-centered profession — and one where modern technology and AI can amplify what great advisors do best.</p>
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {VALUE.map((v) => (
              <div key={v.title} className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="value-card">
                <span className="w-10 h-10 rounded-xl bg-[#006090]/10 text-[#006090] flex items-center justify-center"><v.icon className="w-5 h-5" /></span>
                <h3 className="mt-3 font-semibold text-slate-900 text-sm">{v.title}</h3>
                <p className="mt-1.5 text-xs text-slate-600 leading-relaxed">{v.body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* How it works */}
        <section className="mt-20">
          <h2 className="font-display text-2xl font-bold text-slate-900">How It Works</h2>
          <div className="mt-8 grid md:grid-cols-3 gap-6">
            {STEPS.map((s, i) => (
              <div key={i} className="relative rounded-2xl border border-slate-200 bg-white p-6" data-testid={`step-${i + 1}`}>
                <div className="flex items-center gap-3">
                  <span className="w-9 h-9 rounded-full bg-[#013A5A] text-white flex items-center justify-center font-bold text-sm">{i + 1}</span>
                  <s.icon className="w-5 h-5 text-[#0D9488]" />
                </div>
                <h3 className="mt-4 font-semibold text-slate-900">{s.title}</h3>
                <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="mt-16 rounded-2xl bg-[#013A5A] text-white p-8 sm:p-10 flex flex-col sm:flex-row items-center justify-between gap-6">
          <div>
            <h3 className="font-display text-2xl font-bold">Ready to explore your options?</h3>
            <p className="text-white/75 mt-1.5 text-sm max-w-lg">Create an account to learn more. New accounts are reviewed and approved before full portal access is granted.</p>
          </div>
          <Link to="/register"><Button data-testid="cta-create-account" className="bg-[#0D9488] hover:bg-[#0b7d72] rounded-full px-7 h-12 text-base whitespace-nowrap">Create an Account</Button></Link>
        </section>
      </main>

      <PublicFooter disclaimer={info?.disclaimer} />
    </div>
  );
}
